import {
  ColDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ISetFilterParams,
  MenuItemDef,
  Module,
  ProcessCellForExportParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import {
  HeliosColumnTypes,
  UpdateStatus,
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  heliosClientSideRowConfig,
  heliosDefaultColWidth,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import {
  BulkTransactionModalMode,
  BulkTransactionsModalService,
} from '@helios/create-transactions-modal'
import {
  TransactionsStatusBarModule,
  transactionsStatusBarPanel,
} from '@helios/generic-components'
import { handleColumnVisibilityChange } from '@helios/server-side-util'
import { RootState, selectUserPolicy } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { formatTooltip } from '@helios/utils'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { NfColumnTypes } from '@northfork/ag-grid'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { BulkClearwaterTransactionsKey } from '../../../panel-key'
import {
  BulkClearwaterTransaction,
  BulkCwTranUpdateActionType,
  transactionField as field,
  getUniqueRowId,
} from '../../models/clearwater-transaction.model'
import { BulkClearwaterTransactionsTaskService } from '../../services/bulk-clearwater-transactions-task.service'
import {
  checkBulkTransferOutNonEditableTransaction,
  isTransactionIgnoredForCopy,
} from '../../utils/bulk-clearwater-transactions-util'

const BulkCWTransactionMainGridKey = `${BulkClearwaterTransactionsKey}-MainGrid`

export interface TxToUpdateStatus {
  uniqueTranIds: string[]
  requestStatus: UpdateStatus
}

@Component({
  selector: 'hs-bulk-clearwater-transactions-grid',
  templateUrl: './bulk-clearwater-transactions-grid.component.html',
  styleUrls: ['./bulk-clearwater-transactions-grid.component.scss'],
})
export class BulkClearwaterTransactionsGridComponent
  implements OnInit, OnDestroy
{
  @ViewChild('searchPopoverComponent') searchPopoverComponent?: NgbPopover

  gridModules: Module[] = [RangeSelectionModule, TransactionsStatusBarModule]
  gridOptions = this.createGridOptions()
  gridApi?: GridApi
  destroySubject$ = new Subject<void>()
  focusedTranIds: number[] = []
  cancelRequestTitle = 'Cancel Find transactions request'

  @Output() cancel = new EventEmitter()
  @Input() set isSearchPopoverShown(isShown: boolean) {
    if (isShown) {
      this.searchPopoverComponent?.open()
    } else {
      this.searchPopoverComponent?.close()
    }
  }
  @Input()
  set updateStatuses(updates: TxToUpdateStatus | null) {
    if (updates == null) return
    if (this.gridApi == null) return
    const updatedRows = []
    for (const uniqueTranId of updates.uniqueTranIds) {
      const node = this.gridApi.getRowNode(uniqueTranId)
      if (node == null) continue
      const updatedData = {
        ...node.data,
        [field('isEdited')]: updates.requestStatus,
      }
      updatedRows.push(updatedData)
    }
    const transaction = { update: updatedRows }
    this.gridApi.applyTransaction(transaction)
  }

  hasPermissionForBulkActions = false
  readonly hasAccessBulkToolActions$: Observable<boolean> = this.store
    .select(selectUserPolicy)
    .pipe(
      map(userPolicy => {
        return userPolicy?.bulkToolActions ?? false
      }),
    )

  @Input() data: BulkClearwaterTransaction[] = []
  @Input() isLoading = false
  @Input() transactionsLimitExceeded = false
  @Input() maxTransactions?: number

  @Output() isSearchPopoverShownChange = new EventEmitter<boolean>()
  @Output() bulkEditTxAction = new EventEmitter<BulkClearwaterTransaction[]>()
  @Output() bulkCopyTxAction = new EventEmitter<BulkClearwaterTransaction[]>()
  @Output() clearAction = new EventEmitter<BulkClearwaterTransaction[]>()
  @Output() clearAndSetPostDateAction = new EventEmitter<
    BulkClearwaterTransaction[]
  >()
  @Output() unclearAction = new EventEmitter<BulkClearwaterTransaction[]>()
  @Output() deleteAction = new EventEmitter<BulkClearwaterTransaction[]>()
  @Output() deleteAndIgnoreAction = new EventEmitter<
    BulkClearwaterTransaction[]
  >()
  @Output() deleteAndHoldAction = new EventEmitter<
    BulkClearwaterTransaction[]
  >()
  @Output() clearTable = new EventEmitter<void>()

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly bulkTransactionsModalService: BulkTransactionsModalService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly store: Store<RootState>,
    public taskService: BulkClearwaterTransactionsTaskService,
  ) {}

  ngOnInit() {
    this.hasAccessBulkToolActions$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(bulkToolsPermission => {
        this.hasPermissionForBulkActions = bulkToolsPermission
      })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  onSearchPopoverShown() {
    this.isSearchPopoverShownChange.emit(true)
  }

  onSearchPopoverHidden() {
    this.isSearchPopoverShownChange.emit(false)
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosMultiRowSelectionConfig,
      ...heliosClientSideRowConfig,
      allowContextMenuWithControlKey: true,
      columnDefs: this.getColDefs(),
      getContextMenuItems: params => this.getContextMenuItems(params),
      statusBar: transactionsStatusBarPanel,
      defaultColDef: {
        ...heliosClientSideRowConfig.defaultColDef,
        filterParams: {
          defaultToNothingSelected: false,
        },
      },
      getRowNodeId: (tx: BulkClearwaterTransaction) => getUniqueRowId(tx),
      onGridReady: event => this.onGridReady(event),
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'ClearwaterTransactions'),
      rowClassRules: {
        'font-italic': params => {
          return params.data?.tranSource === 'Synthetic'
        },
      },
      ...disableChangeDetectionOnScroll(this.changeDetector),
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (params.column.getColDef().type === 'Date') {
          return params.value?.toLocaleDateString('en-US')
        }
        return params.value
      },
    }
  }

  /**
   * Right-click menu
   *
   * public for testing reasons
   *
   * @internal
   */
  getContextMenuItems(params: GetContextMenuItemsParams) {
    const menuItems: ('separator' | MenuItemDef)[] = []

    let rowsSelected: BulkClearwaterTransaction[]
    const selectedRowsForEdit: BulkClearwaterTransaction[] = []
    if (!params?.node?.id) {
      return menuItems
    }

    params.api.forEachNodeAfterFilterAndSort(node => {
      if (node.isSelected()) selectedRowsForEdit.push(node.data)
    })

    if (params.api.getSelectedNodes().length > 1) {
      rowsSelected = params.api.getSelectedNodes().map(t => t.data) ?? []
    } else {
      rowsSelected = [params.node.data] as BulkClearwaterTransaction[]
    }

    const transaction: BulkClearwaterTransaction = params?.node?.data ?? {}
    const isBulkTransferOutNonEditableTransaction =
      checkBulkTransferOutNonEditableTransaction(rowsSelected)
    const isTransactionIgnored = isTransactionIgnoredForCopy(rowsSelected)
    const suffix =
      rowsSelected.length > 1 ? ' (' + rowsSelected.length + ')' : ''
    menuItems.push(
      {
        name: 'Clear' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.CLEAR,
          ) || !this.hasPermissionForBulkActions,
        action: () => this.clearAction.emit(rowsSelected),
      },
      {
        name: 'Clear & Set Post Date' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.CLEAR_AND_UPDATE_POST,
          ) || !this.hasPermissionForBulkActions,
        action: () => this.clearAndSetPostDateAction.emit(rowsSelected),
      },
      {
        name: 'Unclear' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.UNCLEAR,
          ) || !this.hasPermissionForBulkActions,
        action: () => this.unclearAction.emit(rowsSelected),
      },
      {
        name: 'Delete' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.DELETE,
          ) || !this.hasPermissionForBulkActions,
        action: () => this.deleteAction.emit(rowsSelected),
      },
      {
        name: 'Delete & Ignore' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.DELETE_AND_IGNORE,
          ) || !this.hasPermissionForBulkActions,
        action: () => this.deleteAndIgnoreAction.emit(rowsSelected),
      },
      {
        name: 'Delete & Hold' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.DELETE_AND_HOLD,
          ) || !this.hasPermissionForBulkActions,
        action: () => this.deleteAndHoldAction.emit(rowsSelected),
      },
      'separator',
      {
        name: 'Edit Note' + suffix,
        disabled: !transaction.availableActions?.includes(
          BulkCwTranUpdateActionType.EDIT_NOTES,
        ),
        action: () => this.taskService.editNotes(rowsSelected),
      },
      {
        name: 'Delete Note' + suffix,
        disabled:
          !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.EDIT_NOTES,
          ) || !rowsSelected.some(tran => tran.tranNote),
        tooltip: !transaction.availableActions?.includes(
          BulkCwTranUpdateActionType.EDIT_NOTES,
        )
          ? 'You do not have permissions to delete note'
          : !rowsSelected.some(tran => tran.tranNote)
          ? 'No notes available for the selected transaction(s).'
          : '',
        action: () => this.taskService.deleteNotes(rowsSelected),
      },
    )

    const isTransSameCategory = this.checkTransactionsCategory(rowsSelected)

    menuItems.push({
      name: 'Copy Transaction' + suffix,
      disabled:
        !isTransSameCategory ||
        !this.hasPermissionForBulkActions ||
        isBulkTransferOutNonEditableTransaction ||
        isTransactionIgnored ||
        !transaction.availableActions?.includes(
          BulkCwTranUpdateActionType.EDIT_TRAN,
        ),
      tooltip: !isTransSameCategory
        ? 'All transactions should be of same category'
        : isBulkTransferOutNonEditableTransaction
        ? 'Single sided and two sided TRNO cannot be edited together'
        : isTransactionIgnored
        ? 'Copy not supported for one of the transaction'
        : !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.EDIT_TRAN,
          )
        ? 'You do not have permissions to copy transaction'
        : '',
      action: () => this.bulkCopyTxAction.emit(rowsSelected),
    })

    menuItems.push({
      name: 'Edit Transaction' + suffix,
      disabled:
        !isTransSameCategory ||
        !this.hasPermissionForBulkActions ||
        isBulkTransferOutNonEditableTransaction ||
        !transaction.availableActions?.includes(
          BulkCwTranUpdateActionType.EDIT_TRAN,
        ),
      tooltip: !isTransSameCategory
        ? 'All transactions should be of same category'
        : isBulkTransferOutNonEditableTransaction
        ? 'Single sided and two sided TRNO cannot be edited together'
        : !transaction.availableActions?.includes(
            BulkCwTranUpdateActionType.EDIT_TRAN,
          )
        ? 'You do not have permissions to edit transaction'
        : '',
      action: () => this.bulkEditTxAction.emit(selectedRowsForEdit),
    })

    return menuItems
  }

  checkTransactionsCategory(
    selectedRows: BulkClearwaterTransaction[],
  ): boolean {
    const firstCategory = selectedRows[0].category
    if (
      firstCategory == 'TRANSFER' &&
      selectedRows.every(
        row => row.type == 'EXCHANGE_IN' || row.type == 'TRANSFER_IN',
      )
    ) {
      // Allow editing of one-sided TRNI and EXIN at the same time - PVAL-21770
      return true
    } else {
      for (const row of selectedRows) {
        if (firstCategory == 'TRANSFER' && selectedRows[0].type != row.type) {
          return false
        } else if (firstCategory != row.category) return false
      }
    }
    return true
  }

  checkIfSameTransactionCategory(
    selectedRows: BulkClearwaterTransaction[],
  ): boolean {
    let oldCategory: string | undefined
    for (const row of selectedRows) {
      const newCategory = row.category
      if (oldCategory && oldCategory != newCategory) {
        return false
      }
      oldCategory = newCategory
    }
    return true
  }

  getBulkCwTxSetFilterValues() {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        refreshValuesOnOpen: true,
        defaultToNothingSelected: true,
        values: undefined,
        closeOnApply: true,
      } as ISetFilterParams,
    }
  }

  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api

    this.savedViewService.manageSavedView(
      event,
      BulkCWTransactionMainGridKey,
      this.destroySubject$,
    )

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)
  }

  clearFilters() {
    this.clearTable.emit()
  }

  onCancelRequest() {
    this.cancel.next()
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: field('isEdited'),
        type: HeliosColumnTypes.BulkIsEdited,
        sortable: true,
      },
      {
        field: field('searchByIdIndex'),
        headerName: 'Order ID',
        width: heliosDefaultColWidth.Id,
      },
      {
        field: field('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        ...this.getBulkCwTxSetFilterValues(),
      },
      {
        field: field('destAccountId'),
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        headerName: 'Transfer Account ID',
        ...this.getBulkCwTxSetFilterValues(),
      },
      {
        ...this.getBulkCwTxSetFilterValues(),
        field: field('type'),
      },
      {
        ...this.getBulkCwTxSetFilterValues(),
        field: field('linkedStatus'),
      },
      {
        field: field('entryDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
      },
      {
        field: field('settleDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
      },
      {
        field: field('postDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
      },
      {
        field: field('clearedDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
      },
      {
        field: field('amount'),
        valueFormatter: params =>
          params.data.isHlpAccount
            ? this.formatNumber(params, 4)
            : this.formatNumber(params, 2),
      },
      {
        field: field('units'),
        valueFormatter: params =>
          params.data.isHlpAccount
            ? this.formatNumber(params, 4)
            : this.formatNumber(params, 2),
      },
      {
        field: field('createdDate'),
        type: NfColumnTypes.Date,
      },
      {
        field: field('modifiedDate'),
        type: NfColumnTypes.Date,
      },
      {
        field: field('sourceId'),
        headerName: 'Source ID',
        width: heliosDefaultColWidth.Id,
      },
      {
        field: field('securityId'),
        headerName: 'Security ID',
        width: heliosDefaultColWidth.Id,
      },
      {
        field: field('secIdentifier'),
        headerName: 'Asset ID',
        width: heliosDefaultColWidth.Id,
      },
      {
        headerName: 'Transaction ID',
        field: field('transactionId'),
        width: heliosDefaultColWidth.Id,
      },
      {
        field: field('originalId'),
        headerName: 'Orig Transaction ID',
        width: heliosDefaultColWidth.Id,
      },
      {
        field: field('currency'),
        width: heliosDefaultColWidth.MediumIcon,
      },
      {
        ...this.getBulkCwTxSetFilterValues(),
        field: field('category'),
      },
      {
        field: field('custodyLinkedStatus'),
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.SmallIcon,
        sortable: true,
      },
      {
        field: field('tradingLinkedStatus'),
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.SmallIcon,
        sortable: true,
      },
      {
        field: field('isLibTransaction'),
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.SmallIcon,
        sortable: true,
      },
      {
        field: field('basis'),
        headerName: 'Transaction Basis',
      },
      {
        ...this.getBulkCwTxSetFilterValues(),
        field: field('secTypeCode'),
        width: heliosDefaultColWidth.MediumIcon,
      },
      {
        ...this.getBulkCwTxSetFilterValues(),
        field: field('tranTypeCode'),
        width: heliosDefaultColWidth.MediumIcon,
        valueFormatter: params => {
          const value: string = params.value ?? ''
          if (params.node == null) return value
          const { userId, typeLabelId, tranTypeCode } = params.node
            .data as BulkClearwaterTransaction
          const PERSEUS_ID = 7333
          const CASH_CONSOLIDATION = '-1046'
          if (
            userId === PERSEUS_ID &&
            typeLabelId === CASH_CONSOLIDATION &&
            tranTypeCode === 'WIRE'
          )
            return 'CCWIRE'
          return value
        },
      },

      {
        field: field('revisionId'),
        headerName: 'Revision ID',
        width: heliosDefaultColWidth.SmallIcon,
        initialHide: true,
      },
      {
        field: field('id'),
        headerName: 'Transaction UUID',
        width: heliosDefaultColWidth.Id,
        initialHide: true,
      },
      {
        field: field('active'),
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.SmallIcon,
        initialHide: true,
        sortable: true,
      },
      {
        field: field('userId'),
        headerName: 'User ID',
        width: heliosDefaultColWidth.Id,
        initialHide: true,
      },
      {
        field: field('tranData'),
        initialHide: true,
        valueGetter: params => JSON.stringify(params.data.tranData),
        tooltipValueGetter: params => formatTooltip(params.data.tranData),
      },
      {
        field: field('dataMap'),
        headerName: 'Collateral Broker',
        width: heliosDefaultColWidth.Id,
        valueGetter: params => params.data.dataMap.collateralBroker,
      },
      {
        field: field('price'),
        initialHide: true,
        type: NfColumnTypes.Number,
      },
      {
        field: field('dataMap'),
        headerName: 'Loan Origination Fee',
        initialHide: true,
        type: NfColumnTypes.Number,
        valueGetter: params => params.data.dataMap.originationFee,
      },
      {
        field: field('dataMap'),
        headerName: 'Loan Prepaid Interest',
        initialHide: true,
        type: NfColumnTypes.Number,
        valueGetter: params => params.data.dataMap.prepaidInterest,
      },
      {
        field: field('dataMap'),
        headerName: 'Loan Past Due Interest',
        initialHide: true,
        type: NfColumnTypes.Number,
        valueGetter: params => params.data.dataMap.interestDue,
      },
      {
        field: field('tranNote'),
        headerName: 'Tran Note',
      },
      {
        field: field('isCapitalize'),
        headerName: 'Capitalized',
      },
    ]
  }
  formatNumber(params: ValueFormatterParams, precision: number): string {
    return params.value
      ? Number(params.value).toLocaleString(undefined, {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        })
      : params.value
  }

  async createBulkActionButton(label: string) {
    if (!this.hasPermissionForBulkActions) return
    switch (label) {
      case 'Trade':
        this.bulkTransactionsModalService.toggleTradeEntryModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Cash Entry':
        this.bulkTransactionsModalService.toggleCashEntryModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Direct Transfer':
        this.bulkTransactionsModalService.toggleDirectTransferModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Currency Forward':
        this.bulkTransactionsModalService.toggleFxForwardModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Fx Spot':
        this.bulkTransactionsModalService.toggleFxSpotTradeModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Wire Transfer':
        this.bulkTransactionsModalService.toggleWireTransferModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Security Exchange':
        this.bulkTransactionsModalService.toggleSecurityExchangeModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Security Transfer In':
        this.bulkTransactionsModalService.toggleSecurityTransferInModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break
      case 'Security Transfer Out':
        this.bulkTransactionsModalService.toggleSecurityTransferOutModal({
          modalMode: BulkTransactionModalMode.CreateCWTran,
        })
        break

      default:
        break
    }
  }
}
